import { SocialServiceUserTasksWidgetStore } from './social-service-user-tasks-widget.store';
import { Component, computed, inject, input } from '@angular/core';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { ButtonComponent } from '../../kit/button/button.component';
import { Divider } from '@openng/optimus-ui/divider';
import { TextInputComponent } from '../../kit/input/text-input/text-input.component';
import { AppFormGroup } from '../../../utils/form.utils';
import { fb, router, translate } from '../../../modules/shared/shared.global-service';
import { isInvalid } from '../../../modules/shared/helpers/validate';
import { UserTaskCreateInput } from '@libs/types/src/services/input-type/user/user-task-create.input';
import { createTaskFrag, updateTaskFrag } from '../user/user-tasks/user-tasks/user-tasks.query';
import { contentGlobalSuccess, successCreated } from '../../../modules/content/content.error';
import { Operator } from '../../../../../../data-grid/src/grid/grid-request.dto';
import { InfiniteScrollStore } from '../../../modules/shared/store/common/infinite-scroll.store';
import { UserTaskObject } from '@libs/types/src/services/object-type/user/user-task.object';
import { EmptyStateComponent } from '../../../modules/shared/components/empty-state/empty-state.component';
import {
    InfiniteScrollLoadOnScreenComponent,
} from '../../kit/infinite-scroll/infinite-scroll-load-on-screen/infinite-scroll-load-on-screen.component';
import { IconComponent } from '../../kit/icon/icon.component';
import {
    EditUserTaskItemDialogComponent,
} from '../user/user-tasks/edit-user-task-item-dialog/edit-user-task-item-dialog.component';
import { BaseDialogService } from '../../kit/base-dialog/base-dialog-service';
import { getUserTaskWidgetFrag } from './social-service-user-tasks-widget.query';
import { UserChipComponent } from '../../kit/user-chip/user-chip.component';
import { UserChipRenderTypeEnum } from '../../kit/user-chip/user-chip.component.types';
import { ConfirmBuilder } from '../../../modules/shared/service/confirm.builder';
import { folderUri } from '../../../utils/router.utils';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { isOperationAllowedForService } from '../../../modules/content/features/service-dashboard/service-dashboard-widget-policies';
import { featureOn } from '../../../feature-flag';

@Component({
    selector: 'app-social-service-user-tasks-widget',
    templateUrl: './social-service-user-tasks-widget.component.html',
    styleUrl: './social-service-user-tasks-widget.component.scss',
    standalone: true,
    providers: [SocialServiceUserTasksWidgetStore],
    imports: [
        ButtonComponent,
        Divider,
        TextInputComponent,
        EmptyStateComponent,
        InfiniteScrollLoadOnScreenComponent,
        IconComponent,
        UserChipComponent,

    ],
})
export class SocialServiceUserTasksWidgetComponent {

    public readonly store = inject(SocialServiceUserTasksWidgetStore);
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);

    public items = input<{ serviceId: string, widget: UserWidgetSetting }[]>([]);
    protected readonly canCreate = computed(() => !featureOn.SOCIAL_SERVICE_DASHBOARD
        || this.items().some(item => isOperationAllowedForService(
            this.store.globalGlobalStore.loggedUserStore.rules(),
            'task.create',
            Number(item.serviceId),
        )));

    protected form = new AppFormGroup({
        newTask: fb.control<string | undefined>(undefined),
    });

    public tasks = computed(() => {
        const currentUserId = this.store.globalGlobalStore.loggedUserStore.currentUserId();

        if (!currentUserId) return null;

        const filter = new FilterBuilder();
        filter.where('completedAt', 'true', Operator.EMPTY);
        filter.where('userId', this.store.globalGlobalStore.loggedUserStore.currentUserId());
        filter.sort('createdAt', 'DESC');

        return new InfiniteScrollStore<UserTaskObject>(
            this.store.api.getUserTasks,
            filter.filter(),
            false,
            getUserTaskWidgetFrag,
        );
    });

    constructor() {
        this.store.onSuccess(
            () => this.tasks()?.refresh(),
            'createUserTask',
            'updateUserTask',
        );
    }

    protected toggleCompleteItem(item: UserTaskObject, event: any) {
        this.confirm.confirm(
            'userTasks.item.messages.completeConfirmationLabel',
            null,
            async () => {
                await this.store.api.updateUserTask({
                    id: item.id,
                    completedAt: new Date().toISOString(),
                }, updateTaskFrag);
                contentGlobalSuccess.next('userTasks.item.completed');
            },
            null,
            'pi-check-circle',
            event?.target,
        );
    }

    protected openDetail(item: UserTaskObject) {
        this.dialogService.open(EditUserTaskItemDialogComponent,
            {
                header: translate.instant('userTasks.title.detail'),
                styleClass: 'p-dialog-width-60',
                data: { item },
            });
    }

    protected showFolderDetail(item: UserTaskObject) {
        if (item.folder) {
            router.navigate([folderUri(item.folder.id, item.folder.serviceId)]).then();
        }
    }

    protected async addTask(event: any | null = null) {
        if (isInvalid(this.form)) return;

        await this.store.api.createUserTask(<UserTaskCreateInput>{
            folderId: null,
            title: this.form.value.newTask || '',
        }, createTaskFrag);

        this.form.reset();

        if (event) {
            event.srcElement.blur();
        }
        successCreated();
    }

    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
